<template>
  <div>
    <v-dialog
      v-model="show"
      width="90%"
      max-width="600"
      scrollable
    >
      <v-card>
        <v-card-title>
          Employee Positions
          <v-spacer></v-spacer>
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            label="Search"
            single-line
            hide-details
            dense
            class="mr-4"
          ></v-text-field>
          <v-btn
            icon
            @click.prevent="show = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="py-3">
          <v-data-table
            dense
            fixed-header
            :headers="tableHeaders"
            :items="statuses"
            :search="search"
            :items-per-page="10"
            :footer-props="{
              'items-per-page-options': [10, 20, 50, -1]
            }"
          >
            <template v-slot:[`item.Status_Descr`]="{ item }">
              <v-text-field
                dense
                hide-details="auto"
                v-if="item.onEdit"
                v-model="item.Status_Descr"
                :rules="[rules.englishOnly]"
                @input="validateCyrillic(item)"
              ></v-text-field>
              <span v-else>{{ item.Status_Descr }}</span>
            </template>
            <template v-slot:[`item.Is_Active`]="{ item }">
              <v-checkbox
                dense
                hide-details
                class="ml-6"
                v-model="item.Is_Active"
                :readonly="!item.onEdit"
              ></v-checkbox>
            </template>
            <template v-slot:[`item.Can_Delete`]="{ item }">
              <v-btn
                small
                color="success"
                icon
                v-if="item.onEdit"
                :disabled="item.hasCyrillic"
                @click="updateStatusDesc(item)"
              >
                <v-icon>mdi-check-circle</v-icon>
              </v-btn>
              <v-btn
                color="primary"
                small
                icon
                :disabled="editMode && !item.onEdit"
                @click="toggleEditItem(item)"
              >
                <v-icon>
                  {{ item.onEdit ? 'mdi-close-circle' : 'mdi-pencil-circle' }}
                </v-icon>
              </v-btn>
              <v-btn
                small
                icon
                color="red darken-2"
                v-if="!item.onEdit"
                :disabled="!item.Can_Delete || editMode"
                @click="toggleDeleteDialog(item.Status_ID)"
              >
                <v-icon>mdi-delete-circle</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="success"
            small
            @click="createNewPositionDialog = !createNewPositionDialog"
          >
            Create New
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      max-width="500"
      v-model="createNewPositionDialog"
    >
      <v-card>
        <v-card-title>
          New Position
          <v-spacer></v-spacer>
          <v-btn
            icon
            @click.prevent="createNewPositionDialog = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="py-3">
          <v-row>
            <v-col cols="12" md="10" lg="10">
              <v-text-field
                dense
                hide-details="auto"
                label="Position"
                counter="100"
                autofocus
                :rules="rules100.concat([rules.englishOnly])"
                v-model="newStatusDescription"
              ></v-text-field>
            </v-col>
            <v-col class="pt-0" cols="12" md="2" lg="2">
              <h5>Is Active</h5>
              <v-checkbox
                class="mt-0 mx-3"
                v-model="newStatusActive"
              ></v-checkbox>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            small
            color="primary"
            :disabled="!newStatusDescription || newStatusDescription.length > 100 || /[а-яА-ЯЁёҐґЄєІіЇї]/.test(newStatusDescription)"
            @click="createNewStatus"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <ConfirmOrDeniedDialog
      v-if="deleteStatusDialog"
      v-model="deleteStatusDialog"
      :mode="confirmOrDeniedDialogModes.delete"
      @onDeleteClick="deleteStatus"
      @onCancelClick="cancelDeleteStatus"
      @onCloseClick="cancelDeleteStatus"
    />
  </div>
</template>
<script>
import { getApiErrorMessage } from '@/utils/apiError'

export default {
  name: 'EmployeeStatusesManager',
  components: {
    ConfirmOrDeniedDialog: () => import('@/components/global/ConfirmOrDeniedDialog.vue')
  },
  props: {
    value: {
      required: false,
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      statuses: [],
      previousStatusDescr: [],
      search: '',
      createNewMode: false,
      editMode: false,
      deleteStatusDialog: false,
      statusIDToDelete: null,
      newStatusDescription: '',
      newStatusActive: true,
      rules: {
        englishOnly: value => {
          if (!value) return true
          const hasCyrillic = /[а-яА-ЯЁёҐґЄєІіЇї]/.test(value)
          return !hasCyrillic || 'English only'
        }
      },
      rules100: [
        v => !v || v.length <= 100 || 'Max 100 characters'
      ],
      tableHeaders: [
        {
          text: 'Position',
          value: 'Status_Descr',
          divider: true,
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap'
        },
        {
          text: 'Is Active',
          value: 'Is_Active',
          divider: true,
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap'
        },
        {
          text: '',
          align: 'center',
          width: 100,
          sortable: false,
          value: 'Can_Delete'
        }
      ]
    }
  },
  computed: {
    user () {
      return this.$store.getters.getUser
    },
    confirmOrDeniedDialogModes () {
      return this.$store.getters.getConfirmOrDeniedDialogModes
    },
    show: {
      get () {
        if (this.value) {
          this.getStatuses()
        }
        return this.value
      },
      set (value) {
        this.updateEmployeeStatuses()
        this.$emit('input', value)
      }
    },
    createNewPositionDialog: {
      get () {
        return this.createNewMode
      },
      set (value) {
        if (!value) {
          this.newStatusDescription = ''
          this.newStatusActive = true
        }
        this.createNewMode = value
      }
    }
  },
  methods: {
    validateCyrillic (item) {
      item.hasCyrillic = /[а-яА-ЯЁёҐґЄєІіЇї]/.test(item.Status_Descr)
    },
    toggleDeleteDialog (statusID) {
      this.statusIDToDelete = statusID
      this.deleteStatusDialog = true
    },
    cancelDeleteStatus () {
      this.statusIDToDelete = null
      this.deleteStatusDialog = false
    },
    createNewStatus () {
      this.axios.post('/api/add_employee_status', {
        Status_Descr: this.newStatusDescription,
        Is_Active: this.newStatusActive
      }, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.createNewPositionDialog = false
        this.getStatuses()
        this.$store.dispatch('setSuccess', { successMsg: 'New position successfully created' })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    updateStatusDesc (item) {
      this.axios.put('/api/update_employee_status_descr', {
        Status_ID: item.Status_ID,
        Status_Descr: item.Status_Descr,
        Is_Active: item.Is_Active
      }, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        const ind = this.previousStatusDescr.findIndex(psd => psd.Status_ID === item.Status_ID)
        this.previousStatusDescr[ind].Status_Descr = item.Status_Descr
        this.previousStatusDescr[ind].Is_Active = item.Is_Active
        item.onEdit = false
        this.editMode = false
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
        item.onEdit = false
      })
    },
    toggleEditItem (item) {
      item.onEdit = !item.onEdit
      this.editMode = !this.editMode
      if (!item.onEdit) {
        const ind = this.previousStatusDescr.findIndex(psd => psd.Status_ID === item.Status_ID)
        item.Status_Descr = this.previousStatusDescr[ind].Status_Descr
        item.Is_Active = this.previousStatusDescr[ind].Is_Active
        item.hasCyrillic = false
      }
    },
    getStatuses () {
      this.axios.get('/api/employee_statuses_to_manage', {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.statuses = res.data
        this.previousStatusDescr = []
        for (let i = 0; i < this.statuses.length; i++) {
          this.$set(this.statuses[i], 'onEdit', false)
          this.$set(this.statuses[i], 'hasCyrillic', false)
          this.previousStatusDescr.push({
            Status_ID: this.statuses[i].Status_ID,
            Status_Descr: this.statuses[i].Status_Descr,
            Is_Active: this.statuses[i].Is_Active
          })
        }
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    deleteStatus () {
      this.axios.delete(`/api/delete_employee_status/${this.statusIDToDelete}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.getStatuses()
        this.cancelDeleteStatus()
        this.$store.dispatch('setSuccess', { successMsg: 'Position successfully deleted' })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    updateEmployeeStatuses () {
      this.axios.get('/api/employee_status', {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.$store.dispatch('setStatuses', { statuses: res.data })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    }
  }
}
</script>
